import { useEffect, useMemo, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import Page from "./Page";
import DateField from "../elements/Date";
import Select from "../elements/Select";
import Input from "../elements/Input";
import Pagination from "../elements/Pagination";
import EntityList from "./EntityList";
import useData from "../../hooks/useData";
import usePage from "../../hooks/usePage";

const OrdersViewExportPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { executeAction, addToWorkQueue } = useData();
  const { showMessage } = usePage();

  const clientId = location?.state?.clientId || null;
  const clientName = location?.state?.clientName || "";
  const returnPath = location?.state?.returnPath || "/clientorders";
  const returnState = location?.state?.returnState || {};

  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [recreatingFileId, setRecreatingFileId] = useState("");
  const [records, setRecords] = useState([]);
  const [orderBy, setOrderBy] = useState("createdAt");
  const [order, setOrder] = useState("desc");
  const [totalCount, setTotalCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(0);
  const [recordsPerPage, setRecordsPerPage] = useState(50);
  const [filterDraft, setFilterDraft] = useState({
    startDate: null,
    endDate: null,
    poNumber: "",
    repId: "",
    locationId: "",
  });
  const [filterApplied, setFilterApplied] = useState({
    startDate: null,
    endDate: null,
    poNumber: "",
    repId: "",
    locationId: "",
  });
  const [repOptions, setRepOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);

  const canLoad =
    !!clientId &&
    ((!!filterApplied.startDate && !!filterApplied.endDate) ||
      !!String(filterApplied.poNumber || "").trim());

  useEffect(() => {
    if (!canLoad) {
      setRecords([]);
      setTotalCount(0);
      return;
    }

    loadOrders();
  }, [
    clientId,
    pageNumber,
    recordsPerPage,
    filterApplied.startDate,
    filterApplied.endDate,
    filterApplied.poNumber,
    filterApplied.repId,
    filterApplied.locationId,
  ]);

  const applyFilters = () => {
    const startDate = filterDraft.startDate || null;
    const endDate = filterDraft.endDate || null;
    const poNumber = String(filterDraft.poNumber || "").trim();
    const hasDateRange = !!startDate && !!endDate;

    if (!clientId) {
      showMessage("Client context is required", "error");
      return;
    }

    if (!hasDateRange && !poNumber) {
      showMessage("Provide a date range or a PO Number", "error");
      return;
    }

    if (hasDateRange) {
      if (new Date(startDate) > new Date(endDate)) {
        showMessage("From date cannot be after To date", "error");
        return;
      }
    }

    setPageNumber(0);
    const nextFilter = {
      startDate,
      endDate,
      poNumber,
      repId: filterDraft.repId || "",
      locationId: filterDraft.locationId || "",
    };
    setFilterApplied(nextFilter);
    if (hasDateRange) {
      loadFilterOptions(startDate, endDate);
    } else {
      setRepOptions([]);
      setLocationOptions([]);
    }
  };

  const clearFilters = () => {
    setFilterDraft({
      startDate: null,
      endDate: null,
      poNumber: "",
      repId: "",
      locationId: "",
    });
    setFilterApplied({
      startDate: null,
      endDate: null,
      poNumber: "",
      repId: "",
      locationId: "",
    });
    setPageNumber(0);
    setRecords([]);
    setTotalCount(0);
    setRepOptions([]);
    setLocationOptions([]);
  };

  const loadFilterOptions = async (startDate, endDate) => {
    if (!clientId || !startDate || !endDate) {
      setRepOptions([]);
      setLocationOptions([]);
      return;
    }

    try {
      const results = await executeAction({
        entityName: "Order",
        action: "getClientOrderFilterOptions",
        data: {
          clientId,
          startDate,
          endDate,
        },
      });

      if (results?.isSuccessful) {
        setRepOptions(
          Array.isArray(results?.data?.reps) ? results.data.reps : [],
        );
        setLocationOptions(
          Array.isArray(results?.data?.locations) ? results.data.locations : [],
        );
      } else {
        setRepOptions([]);
        setLocationOptions([]);
      }
    } catch {
      setRepOptions([]);
      setLocationOptions([]);
    }
  };

  const loadOrders = async () => {
    if (!canLoad) {
      return;
    }

    setLoading(true);
    try {
      const results = await executeAction({
        entityName: "Order",
        action: "getClientOrdersPage",
        data: {
          clientId,
          startDate: filterApplied.startDate,
          endDate: filterApplied.endDate,
          poNumber: filterApplied.poNumber || undefined,
          repId: filterApplied.repId || undefined,
          locationId: filterApplied.locationId || undefined,
          pageNumber,
          recordsPerPage,
        },
      });

      if (results?.isSuccessful && Array.isArray(results?.data?.records)) {
        const mappedRecords = results.data.records.map((record) => {
          const items = Array.isArray(record?.items) ? record.items : [];
          const orderItems = items.map((item, index) => {
            const qty = Number(item?.quantity || 0);
            const unitPrice = Number(item?.price || item?.unitPrice || 0);
            const amount = Number(item?.amount || qty * unitPrice || 0);
            const skuName =
              item?.sku?.name || item?.name || item?.skuName || "Unnamed SKU";
            const skuCode =
              item?.sku?.clientCode || item?.clientCode || item?.upc || "";

            return {
              name: `${index + 1}. ${skuName}${skuCode ? ` (${skuCode})` : ""} | Qty: ${qty} | Unit: ${unitPrice.toFixed(2)} | Amount: ${amount.toFixed(2)}`,
            };
          });

          return {
            ...record,
            orderItems,
          };
        });

        setRecords(mappedRecords);
        setTotalCount(Number(results?.data?.total || 0));
      } else {
        setRecords([]);
        setTotalCount(0);
        showMessage(results?.message || "Failed to load orders", "error");
      }
    } catch (err) {
      setRecords([]);
      setTotalCount(0);
      showMessage(err?.message || "Failed to load orders", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!clientId || !filterApplied.startDate || !filterApplied.endDate) {
      showMessage("Apply date range first", "error");
      return;
    }

    setExporting(true);
    try {
      const queueResults = await addToWorkQueue("exportClientOrdersCsv", {
        clientId,
        startDate: filterApplied.startDate,
        endDate: filterApplied.endDate,
        poNumber: filterApplied.poNumber || undefined,
        repId: filterApplied.repId || undefined,
        locationId: filterApplied.locationId || undefined,
      });

      if (queueResults?.isSuccessful) {
        showMessage(
          "Orders export queued. You will receive an email shortly.",
          "success",
        );
        return;
      }

      showMessage(queueResults?.message || "Unable to queue export", "error");
    } catch (err) {
      showMessage(err?.message || "Unable to queue export", "error");
    } finally {
      setExporting(false);
    }
  };

  const handleToolbarClick = (name) => {
    if (name === "back") {
      navigate(returnPath, { state: returnState });
      return;
    }

    if (name === "refresh") {
      loadOrders();
      return;
    }

    if (name === "export") {
      handleExport();
    }
  };

  const downloadRecreatedFile = async (transmissionId) => {
    if (!transmissionId) {
      showMessage("No transmission found for this order", "error");
      return;
    }

    setRecreatingFileId(String(transmissionId));
    try {
      const results = await executeAction({
        entityName: "OrderTransmission",
        action: "recreateSentFile",
        data: { transmissionId: String(transmissionId) },
      });

      if (!results?.isSuccessful || !results?.data?.content) {
        showMessage(results?.message || "Unable to recreate file", "error");
        return;
      }

      const fileName = results?.data?.fileName || "order_recreated.csv";
      const mimeType = results?.data?.mimeType || "text/csv;charset=utf-8;";
      const blob = new Blob([results.data.content], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.setAttribute("download", fileName);
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showMessage(err?.message || "Unable to recreate file", "error");
    } finally {
      setRecreatingFileId("");
    }
  };

  const getSortValue = (row, fieldName) => {
    switch (fieldName) {
      case "recreateFile":
        return row?.latestTransmissionId ? 1 : 0;
      case "createdAt":
      case "callDate": {
        const timestamp = new Date(row?.[fieldName] || 0).getTime();
        return Number.isFinite(timestamp) ? timestamp : 0;
      }
      case "totalQty":
      case "totalAmount":
      case "latestSubmissionVersion": {
        const value = Number(row?.[fieldName]);
        return Number.isFinite(value) ? value : Number.NEGATIVE_INFINITY;
      }
      default:
        return row?.[fieldName] ?? "";
    }
  };

  const compareValues = (leftValue, rightValue) => {
    if (typeof leftValue === "number" && typeof rightValue === "number") {
      return leftValue - rightValue;
    }

    return String(leftValue ?? "").localeCompare(String(rightValue ?? ""), undefined, {
      numeric: true,
      sensitivity: "base",
    });
  };

  const sortedRecords = useMemo(() => {
    return [...records].sort((left, right) => {
      const result = compareValues(
        getSortValue(left, orderBy),
        getSortValue(right, orderBy),
      );

      if (result !== 0) {
        return order === "asc" ? result : -result;
      }

      if (orderBy !== "createdAt") {
        return (
          new Date(right?.createdAt || 0).getTime() -
          new Date(left?.createdAt || 0).getTime()
        );
      }

      return 0;
    });
  }, [records, orderBy, order]);

  const handleSortChange = (property, nextOrder) => {
    setOrderBy(property);
    setOrder(nextOrder);
  };

  const columns = useMemo(
    () => [
      {
        name: "poNumber",
        label: "PO #",
        width: "140px",
        render: (row) => row?.poNumber || "",
      },
      {
        name: "recreateFile",
        label: "File",
        width: "120px",
        render: (row) => {
          const transmissionId = row?.latestTransmissionId || "";
          const hasTransmission = !!transmissionId;
          const isBusy =
            hasTransmission &&
            recreatingFileId !== "" &&
            recreatingFileId === String(transmissionId);
          return (
            <Button
              size="small"
              variant="outlined"
              disabled={!hasTransmission || isBusy}
              onClick={(event) => {
                event.stopPropagation();
                downloadRecreatedFile(transmissionId);
              }}
            >
              {!hasTransmission
                ? "Not Transmitted"
                : isBusy
                  ? "Creating..."
                  : "Recreate"}
            </Button>
          );
        },
      },
      {
        name: "createdAt",
        label: "Created",
        width: "170px",
        render: (row) =>
          row?.createdAt ? new Date(row.createdAt).toLocaleString() : "",
      },
      {
        name: "callDate",
        label: "Call Date",
        width: "120px",
        render: (row) => row?.callDate || "",
      },
      {
        name: "repName",
        label: "Rep",
        width: "180px",
        render: (row) => row?.repName || "",
      },
      {
        name: "locationName",
        label: "Location",
        width: "220px",
        render: (row) => row?.locationName || "",
      },
      {
        name: "locationNumber",
        label: "Loc #",
        width: "120px",
        render: (row) => row?.locationNumber || "",
      },
      {
        name: "totalQty",
        label: "Qty",
        width: "90px",
        render: (row) => Number(row?.totalQty || 0),
      },
      {
        name: "totalAmount",
        label: "Amount",
        width: "120px",
        render: (row) => Number(row?.totalAmount || 0).toFixed(2),
      },
      {
        name: "latestSubmissionVersion",
        label: "Submission Ver",
        width: "130px",
        render: (row) => row?.latestSubmissionVersion ?? "",
      },
      {
        name: "latestTransmissionStatus",
        label: "Transmission",
        width: "140px",
        render: (row) => row?.latestTransmissionStatus || "",
      },
      {
        name: "latestTransmissionLastError",
        label: "Last Error",
        width: "320px",
        render: (row) => row?.latestTransmissionLastError || "",
      },
    ],
    [recreatingFileId],
  );

  return (
    <Page
      pageName="Client Orders View & Export"
      entityName={clientName || ""}
      includeToolbar
      onToolItemClick={handleToolbarClick}
      items={[
        { name: "back", label: "Back", iconName: "ArrowBack" },
        { name: "refresh", label: "Refresh", iconName: "Refresh" },
        {
          name: "export",
          label: exporting ? "Queueing" : "Export CSV",
          iconName: "Download",
        },
      ]}
    >
      {!clientId ? (
        <Typography>Client context is missing for this page.</Typography>
      ) : (
        <Box>
          <Typography sx={{ mb: 1 }}>
            View orders, expand rows to see item lines, and export CSV at line
            level.
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr auto auto",
              gap: 1,
              alignItems: "end",
              mb: 1,
            }}
          >
            <DateField
              label="From Date"
              value={filterDraft.startDate}
              onChange={(value) =>
                setFilterDraft((previous) => ({
                  ...previous,
                  startDate: value,
                }))
              }
            />

            <DateField
              label="To Date"
              value={filterDraft.endDate}
              onChange={(value) =>
                setFilterDraft((previous) => ({
                  ...previous,
                  endDate: value,
                }))
              }
            />

            <Select
              label="Rep"
              value={filterDraft.repId}
              items={[{ id: "", name: "All" }, ...repOptions]}
              onChange={(value) =>
                setFilterDraft((previous) => ({
                  ...previous,
                  repId: value,
                }))
              }
            />

            <Select
              label="Location"
              value={filterDraft.locationId}
              items={[{ id: "", name: "All" }, ...locationOptions]}
              onChange={(value) =>
                setFilterDraft((previous) => ({
                  ...previous,
                  locationId: value,
                }))
              }
            />

            <Input
              label="PO Number"
              value={filterDraft.poNumber}
              onChange={(value) =>
                setFilterDraft((previous) => ({
                  ...previous,
                  poNumber: value,
                }))
              }
            />

            <Button
              variant="outlined"
              onClick={clearFilters}
            >
              Clear
            </Button>

            <Button
              variant="contained"
              onClick={applyFilters}
            >
              Apply
            </Button>
          </Box>

          {!canLoad ? (
            <Typography sx={{ mb: 1 }}>
              Select a date range or enter a PO Number, then click Apply to load
              orders.
            </Typography>
          ) : null}

          <Pagination
            totalCount={totalCount}
            pageNumber={pageNumber}
            recordsPerPage={recordsPerPage}
            onPageChange={(newPage) => setPageNumber(newPage)}
            onRecordsPerPageChange={(newRowsPerPage) => {
              setPageNumber(0);
              setRecordsPerPage(Number(newRowsPerPage));
            }}
            styles={{ marginBottom: "6px" }}
          />

          {loading ? (
            <Typography>Loading orders...</Typography>
          ) : (
            <EntityList
              entityList={sortedRecords}
              columns={columns}
              subTable="orderItems"
              tableHeight="calc(100vh - 260px)"
              onSortChange={handleSortChange}
              orderBy={orderBy}
              order={order}
            />
          )}
        </Box>
      )}
    </Page>
  );
};

export default OrdersViewExportPage;
